<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BuzzScoreHistoryChart from './BuzzScoreHistoryChart.vue';
import BuzzScoreCard from './BuzzScoreCard.vue';
import BuzzScoreSignalsChart from './BuzzScoreSignalsChart.vue';
import BuzzScoreFormulaCard from './BuzzScoreFormulaCard.vue';
import BuzzScoreScale from './BuzzScoreScale.vue';
import BuzzScoreDistributionChart from './BuzzScoreDistributionChart.vue';
import BuzzFootfallHistogram from './BuzzFootfallHistogram.vue';
import BuzzReviewsInsight from './BuzzReviewsInsight.vue';
import BuzzMediaInsight from './BuzzMediaInsight.vue';
import BuzzDirectoriesTable from './BuzzDirectoriesTable.vue';
import BuzzFootfallInsight from './BuzzFootfallInsight.vue';
import BuzzMentionsTable from './BuzzMentionsTable.vue';
import BuzzReportSection from './BuzzReportSection.vue';
import BuzzReportPlaceholder from './BuzzReportPlaceholder.vue';
import { resolvePeriodDelta } from './buzzScoreDistribution';

const props = defineProps({
  venue: {
    type: Object,
    required: true,
  },
  buzz: {
    type: Object,
    default: null,
  },
  peerScores: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  noScore: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const score = computed(
  () =>
    props.buzz?.buzz_score ?? props.buzz?.summary?.b ?? props.venue?.buzz_score
);
const avg28d = computed(
  () =>
    props.buzz?.buzz_score_avg_28d ??
    props.buzz?.summary?.bavg_28d ??
    props.venue?.buzz_score_avg_28d
);
const summary = computed(() => props.buzz?.summary || {});
const history = computed(() => props.buzz?.history || []);
const mentions = computed(() => props.buzz?.mentions || []);
const insights = computed(() => props.buzz?.insights || {});

const periodDelta = computed(() =>
  resolvePeriodDelta(
    props.buzz?.bp ?? summary.value.bp,
    history.value,
    score.value
  )
);

const showPlaceholder = computed(() => props.loading || props.noScore);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <h3 class="text-lg font-medium text-n-slate-12 mb-0">
        {{ venue.name }}
      </h3>
      <p v-if="venue.address" class="text-sm text-n-slate-10 mt-1 mb-0">
        {{ venue.address }}
      </p>
    </div>

    <BuzzReportPlaceholder
      v-if="showPlaceholder"
      :pulsing="loading"
      :message="noScore ? t('BUZZ_REPORTS.NO_SCORE_YET') : ''"
    />

    <p v-else-if="error" class="text-sm text-n-ruby-10">
      {{ error }}
    </p>

    <template v-else>
      <div class="grid gap-3 lg:grid-cols-6 lg:items-stretch">
        <div class="flex flex-col gap-3 lg:col-span-2">
          <BuzzScoreCard
            compact
            :score="score"
            :avg28d="avg28d"
            :period-delta="periodDelta"
            :history="history"
            :title="t('BUZZ_REPORTS.SCORE')"
            avg-label-key="BUZZ_REPORTS.HERO.AVG_28D"
            no-score-label-key="OVERVIEW_REPORTS.BUZZ_SCORES.NO_SCORE"
          />
          <BuzzScoreFormulaCard :summary="summary" />
        </div>
        <div class="relative min-h-52 lg:col-span-4 lg:min-h-0 print:min-h-52">
          <div
            class="min-h-52 h-full lg:absolute lg:inset-0 lg:min-h-0 print:relative print:inset-auto print:min-h-52"
          >
            <BuzzScoreSignalsChart class="h-full" :summary="summary" />
          </div>
        </div>
      </div>

      <BuzzReportSection :header="t('BUZZ_REPORTS.SCALE.TITLE')">
        <BuzzScoreScale :score="score" :peer-scores="peerScores" />
      </BuzzReportSection>

      <BuzzReportSection :header="t('BUZZ_REPORTS.DISTRIBUTION.TITLE')">
        <BuzzScoreDistributionChart :score="score" :peer-scores="peerScores" />
      </BuzzReportSection>

      <BuzzReportSection
        :header="t('BUZZ_REPORTS.CATEGORY_REVIEWS')"
        signal="reviews"
      >
        <BuzzReviewsInsight :reviews="insights.reviews || {}" />
      </BuzzReportSection>

      <BuzzReportSection
        :header="t('BUZZ_REPORTS.CATEGORY_MEDIA')"
        signal="media"
      >
        <BuzzMediaInsight :media="insights.media || {}" />
      </BuzzReportSection>

      <BuzzReportSection
        :header="t('BUZZ_REPORTS.INSIGHTS.DIRECTORIES_TITLE')"
        signal="media"
      >
        <BuzzDirectoriesTable :media="insights.media || {}" />
      </BuzzReportSection>

      <BuzzReportSection :header="t('BUZZ_REPORTS.MENTIONS')" signal="media">
        <BuzzMentionsTable :mentions="mentions" />
      </BuzzReportSection>

      <BuzzReportSection
        :header="t('BUZZ_REPORTS.CATEGORY_FOOTFALL')"
        signal="footfall"
      >
        <BuzzFootfallInsight :footfall="insights.footfall || {}" />
        <BuzzFootfallHistogram
          :popular-times="insights.footfall?.popular_times || {}"
        />
      </BuzzReportSection>

      <BuzzReportSection :header="t('BUZZ_REPORTS.HISTORY')">
        <BuzzScoreHistoryChart :history="history" />
      </BuzzReportSection>
    </template>
  </div>
</template>
